import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Collapse,
    Stack,
    Typography,
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import {InputField} from '@/components/common';
import {getScheduleNotes, updateScheduleNotes} from '@/services/auditService/auditFsAnnualService';

export interface WpNotesControlProps {
    companyId?: number | null;
    annualId?: number | null;
    fsId?: number | null;
    initialNotes?: string | null;
    readOnly?: boolean;
    /** Buttons / actions rendered to the right of the Notes icon on the same row. */
    trailing?: React.ReactNode;
    /** Far-right content on the top row (e.g. related-party switch). */
    topRight?: React.ReactNode;
}

/**
 * Compact schedule Notes control for WP table top-left.
 * Click: Accordion-style Collapse + InputField focus for edit.
 * Blur: persist; keep InputField visible when notes non-empty, hide when empty.
 */
export function WpNotesControl({
    companyId,
    annualId,
    fsId,
    initialNotes,
    readOnly = false,
    trailing,
    topRight,
}: WpNotesControlProps) {
    const [notes, setNotes] = useState<string>(initialNotes ?? '');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);

    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const canPersistNotes = Number(companyId) > 0 && Number(annualId) > 0 && Number(fsId) > 0;
    const notesTrimmed = notes.trim();
    const hasNotes = notesTrimmed.length > 0;
    const showControl = canPersistNotes || hasNotes;
    const fieldOpen = editing || hasNotes;

    useEffect(() => {
        setNotes(initialNotes ?? '');
    }, [initialNotes, fsId, annualId, companyId]);

    useEffect(() => {
        let cancelled = false;
        if (!canPersistNotes) return;
        (async () => {
            try {
                const vo = await getScheduleNotes(Number(companyId), Number(annualId), Number(fsId));
                if (!cancelled && vo?.notes != null) {
                    setNotes(String(vo.notes));
                }
            } catch {
                // keep initialNotes / empty
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canPersistNotes, companyId, annualId, fsId]);

    useEffect(() => {
        if (!editing) return;
        const t = window.setTimeout(() => {
            inputRef.current?.focus();
        }, 120);
        return () => window.clearTimeout(t);
    }, [editing]);

    const persistNotes = useCallback(async () => {
        if (!canPersistNotes || readOnly || saving) return;
        setSaving(true);
        try {
            await updateScheduleNotes({
                companyId: Number(companyId),
                annualId: Number(annualId),
                fsId: Number(fsId),
                notes,
            });
        } catch {
            // keep local value; user can retry on next blur
        } finally {
            setSaving(false);
        }
    }, [canPersistNotes, readOnly, saving, companyId, annualId, fsId, notes]);

    const handleIconClick = () => {
        if (readOnly && !canPersistNotes) return;
        setEditing(true);
    };

    const handleEditorBlur = async () => {
        // End edit mode first; field stays open when hasNotes, hides when empty.
        setEditing(false);
        // Collapse only when empty after blur; non-empty notes stay expanded via `hasNotes`.
        await persistNotes();
    };

    // Keep row visible when parent passes trailing/topRight (e.g. Show Petty Cash)
    // even if Notes itself has nothing to persist yet.
    if (!showControl && !trailing && !topRight) return null;

    return (
        <Box sx={{width: '100%', minWidth: 0}}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
                    {showControl && (
                        <Box
                            onClick={handleIconClick}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                userSelect: 'none',
                                color: hasNotes ? 'primary.main' : 'text.primary',
                                '&:hover': {opacity: 0.85},
                            }}
                            role="button"
                            aria-label="schedule notes"
                            aria-expanded={fieldOpen}
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleIconClick();
                                }
                            }}
                        >
                            <NotesIcon fontSize="small" color={hasNotes ? 'primary' : 'inherit'}/>
                            <Typography
                                variant="body2"
                                color={hasNotes ? 'primary' : 'inherit'}
                                sx={{fontWeight: 600}}
                            >
                                Notes
                            </Typography>
                        </Box>
                    )}
                    {showControl ? trailing : null}
                </Stack>
                {topRight}
            </Box>

            <Collapse in={fieldOpen} timeout="auto" unmountOnExit>
                <Box sx={{pt: 1}}>
                    <InputField
                        inputRef={inputRef}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        onBlur={handleEditorBlur}
                        placeholder="Add notes for colleagues…"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={8}
                        align="left"
                        InputProps={{readOnly: readOnly || !canPersistNotes}}
                        inputProps={{maxLength: 4000}}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}

export default WpNotesControl;
